import { useGenerate } from '@/hooks/useGenerate';
import { Module, ModuleRoute } from '@/types/artwork/generate';
import { useArtworkStore } from '@/store/artwork';
import { ModuleEnum, resolveStoreModuleId } from '@/assets/artwork/moduleEnum';
import { useProgress } from '@/hooks/useProgress';
import { $bus } from '@/mitt';
import { initGenerateResult } from '@/hooks/useGenerate';
import { useGeneratingVersions } from '@/hooks/useGeneratingVersions';
import { cloneDeep } from 'lodash';
import { useTaskQueueIntegration } from '@/hooks/usetaskQueue2'
import { getConsumeTime } from '@xmiles/holopix-helper';

interface FinishTrackState {
  client_id?: string;
  result: 0 | 1;
  imageIds?: string;
  queue_time_number?: number;
  loading_time_number?: number;
  vipChannel?: boolean;
}

interface Options {
  getModuleParams: () => any;
  onStartTrack: (clientId: string) => void;
  onFinishTrack: (data: FinishTrackState) => void;
  onClientIdGenerated?: (clientId: string) => void;
  onClientIdCleared?: () => void;
}

export function startModuleGenerate(
  module: Module,
  {
    getModuleParams,
    onStartTrack,
    onFinishTrack,
    onClientIdGenerated,
    onClientIdCleared
  }: Options
) {
  let clientId = '';
  const { start, close, onProgressUpdated } = useProgress();

  const artworkStore = useArtworkStore();
  // 使用 resolveStoreModuleId 归一化 module ID（支持 V2 工作流映射到正确的路由）
  const moduleRouteName = ModuleEnum[resolveStoreModuleId(module)] as ModuleRoute;
  let params = getModuleParams?.() || {};

  if (!params.ctime) {
    params.ctime = new Date().getTime();
  }

  // 文俊特殊处理，当使用了3视图的姿势时，默认在提示词后面加上,reference sheet,
  const isThreeView = [25, 26, 27, 28, 29, 30].includes(params?.layoutReferPose?.id);
  if (isThreeView) {
    params = cloneDeep(params);
    params.prompt += ',reference sheet,';
  }

  const { set, get, updateResult, isModuleLatest } = useGeneratingVersions();

  // 使用路由模块更新 store；params.module 在 V2 工作流中可能是工作流 id（如 50），与路由枚举不一致
  artworkStore[moduleRouteName].result = { ...initGenerateResult };

  // 开始生图交互
  useGenerate(
    params,
    {
      transparentLayerWhite: true,
      onWebSocketInited: (websocket, _clientId, isReconnection) => {
        clientId = _clientId;

        // 首先初始化新队列集成，创建队列任务
        try {
          const integration = useTaskQueueIntegration();
          integration.handleOnWebSocketInited(clientId, module, params);
        } catch (error) {
          // 如果新队列不可用，忽略错误，继续使用旧队列
          console.error('❌ New queue integration error:', error);
        }

        // 队列任务创建后，再通知外部 clientId 已生成
        onClientIdGenerated?.(clientId);

        const oldSocketModel = get(clientId);
        const oldResult = oldSocketModel?.result;
        const result = isReconnection ? oldResult : { ...initGenerateResult };

        set({
          module,
          clientId,
          params,
          result,
          socket: websocket
        });

        !isReconnection && onStartTrack(clientId);
      },
      onUpdate: (messageData) => {
        updateResult(clientId, { ...messageData });

        // 新队列集成 - 安全调用
        try {
          const integration = useTaskQueueIntegration();
          integration.handleOnUpdate(messageData, clientId);
        } catch (error) {
          // 忽略新队列错误
        }

        if (isModuleLatest(clientId)) {
          artworkStore[moduleRouteName].result = { ...artworkStore[moduleRouteName].result, ...messageData };
        }
      },
      onGenerating: () => {
        // 进度更新
        onProgressUpdated((progress) => {
          updateResult(clientId, { progress });
          // 新队列集成 - 安全调用
          try {
            const integration = useTaskQueueIntegration();
            integration.handleOnGenerating(clientId);
          } catch (error) {
            // 忽略新队列错误
          }

          if (isModuleLatest(clientId)) {
            artworkStore[moduleRouteName].result.progress = progress;
          }
        });

        // 开始假进度条
        const estimatedTime = getConsumeTime(module, params);
        start(estimatedTime);
      },
      onSuccess: (messageData) => {
        if (isModuleLatest(clientId)) {
          artworkStore.resetModuleLastVersionParams(module, params);
        }

        // 新队列集成 - 安全调用
        try {
          const integration = useTaskQueueIntegration();
          integration.handleOnSuccess(clientId);
        } catch (error) {
          // 忽略新队列错误
        }

        // 如果当前模块为智能扩图或者涂抹替换，emit事件
        if ([ModuleEnum.replace, ModuleEnum.expansion].includes(module)) {
          setTimeout(() => {
            $bus.emit('showReplaceConfirm');
          }, 200)
        }

        // 通知历史记录侧边栏有新的版本生成
        $bus.emit('hasNewVersion');

        const { queue_time_number, loading_time_number, client_id, vipChannel } = messageData;
        onFinishTrack({
          client_id,
          result: 1,
          imageIds: (messageData.data?.images || [])
            .map(item => item.id)
            .join(','),
          queue_time_number,
          loading_time_number,
          vipChannel,
        });
      },
      onError: (messageData) => {
        if (isModuleLatest(clientId)) {
          const { del } = useGeneratingVersions();
          del(clientId);

          artworkStore.setModuleLatest(module);
        }

        // 新队列集成 - 安全调用
        try {
          const integration = useTaskQueueIntegration();
          integration.handleOnError(clientId, messageData.msg || 'Generation failed');
        } catch (error) {
          // 忽略新队列错误
        }

        const { queue_time_number, loading_time_number, client_id, vipChannel } = messageData;
        onFinishTrack({
          client_id,
          result: 0,
          imageIds: '',
          queue_time_number,
          loading_time_number,
          vipChannel,
        });
      },
      onFinish: () => {
        close();

        onClientIdCleared?.();

        const { del } = useGeneratingVersions();
        del(clientId);
      },
      onClose: () => {
        if (isModuleLatest(clientId, { empty: true })) {
          close();
        }
      }
    }
  );
}